/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useRef, useState} from "react"
import {Checkbox, Collapse} from "antd"
import {v4 as uuid} from "uuid"
import {cloneDeep, isBoolean} from "lodash"
import moment from "moment"
import {useTranslation} from "react-i18next"
import {Model} from "Model"
import {BaseInput, Icon} from "components"
import {handleError, scrollTableToBottom, toastError, toastSuccess, toastWarning} from "helpers"
import {shiftsService, userService} from "services"
import {KlassappTableHOC} from "HOC"
import {ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import {BaseButton, SecondaryButton, AddItemCircleButton} from "components/buttons"
import {KlassappTableProps, Order} from "types/common"
import styles from "./Shifts.module.css"
import {BaseTimePicker} from "components/DateTimePicker"
import CampusSelect from "components/CampusSelect"
import ProgramSelect from "components/ProgramSelect"
import {getDisabledEndHours, getDisabledEndMinutes} from "helpers/calendar"
import {
    INITIAL_WORK_WEEK,
    WEEKDAY_NAMES,
    WorkDay,
    breakTimeFormToPayload,
    breakTimePayloadToForm,
    cloneBreakTimes,
    BreakTimeForm,
    BreakTimesFieldErrorsByDay,
    collectBreakTimesValidationErrors,
    formatBreakTimeFieldErrorLabel,
    orderWorkWeekByStartDay
} from "types/work-schedule"
import {WorkDayItem} from "sections/CalendarSettings/parts/WorkScheduleDetails/parts"
import {ShiftBreakTimesPopup} from "./ShiftBreakTimesPopup"
import ProgramVersionSelect from "components/ProgramVersionSelect"
import {useFilterStorage, useModel, useVisible} from "hooks"
import {useMutation, useQuery} from "@tanstack/react-query"
import {Filter} from "./Filter"
import {FilterKey} from "types/filter"
import {useDebounce} from "react-use"
import {Modules} from "@edular/modules"

const {Panel} = Collapse

type PageProps = {
    t: Function
    model: Model
}

const defaultFilter = {
    programVersions: [],
    campuses: [],
    programs: [],
    isActive: null
}

type Props = KlassappTableProps & PageProps

const ShiftTab = (props: Props) => {
    const {
        isShowTableHeaderAction,
        tableHeaderActions,
        page,
        total,
        pageSize,
        columns,
        data,
        fields,
        allFields,
        isHideMenuActions,
        getCurrentData,
        menuActions,
        dispatch,
        onClickShowConfirmModal,
        onUpdateRowData,
        onUpdateTableData,
        onClickSortColumn,
        onDraggableColumn,
        onChangeFields,
        onChangeAllFields,
        onChangePage,
        dispatchFunc,
        onChangeRowPerPage,
        orderField
    } = props

    const {t} = useTranslation(["academics", "common"])
    const model = useModel()
    const startWeekOn = model.startWeekOn

    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        FilterKey.ShiftSettingFilter,
        defaultFilter
    )

    const [searchDebounce, setSearchDebounce] = useState(searchValue)

    useDebounce(() => setSearchDebounce(searchValue), 500, [searchValue])

    const popupConfirmManualAssignShift = useVisible()
    const bulkBreakTimesPopup = useVisible()
    const bulkBreakTimesWorkWeekRef = useRef<WorkDay[]>([])
    const [bulkBreakTimesInitial, setBulkBreakTimesInitial] = useState<BreakTimeForm[]>([])
    const pendingCloseBulkPopupRef = useRef(false)
    const breakTimesFieldErrorsRef = useRef<BreakTimesFieldErrorsByDay>({})
    const [currentShiftSelected, setCurrentShiftSelected] = useState<{
        shiftId: number
        campusIds: number[]
        majorIds: number[]
        majorVersionIds: number[]
    }>(undefined)
    const [newData, setNewData] = useState<any>(null)
    const currentNewData = useRef<any>()
    const [oldData, setOldData] = useState<any>(null)
    const currentOldData = useRef<any>()

    const getFilterParams = () => {
        const filter: any = {
            search: searchDebounce,
            programIds: currentFilter.programs?.length ? currentFilter.programs.map((program) => program.id) : [],
            programVersionIds: currentFilter.programVersions?.length
                ? currentFilter.programVersions.map((version) => version.id)
                : [],
            campusIds: currentFilter.campuses?.length ? currentFilter.campuses.map((campus) => campus.id) : []
        }

        if (currentFilter.isActive !== null) {
            filter.isActive = currentFilter.isActive
        }

        return filter
    }

    const checkFacialRecognitionQuery = useQuery(["check-facial-recognition"], async () => {
        const listModulesSettings = await userService.getModuleOption()
        const isEnabledFacialRecognition = !!listModulesSettings
            .find((module) => module.moduleId === Modules.Academic)
            ?.children.find((module) => module.moduleId === Modules.AcademicFacialRecognition).access

        return isEnabledFacialRecognition
    })

    const settingsQuery = useQuery(
        ["shifts-settings", page, pageSize, searchDebounce, orderField, startWeekOn],
        async () => {
            const filter = getFilterParams()
            const payload: any = {
                linkedObject: true,
                filter,
                sort: {
                    orderBy: orderField?.field || "isActive",
                    orderDir: orderField?.order || Order.Desc
                }
            }

            const {data, total} = await shiftsService.getAll(payload)
            const timeFormat = model.getUserTimeFormat()
            const todayStr = moment().format("YYYY-MM-DD")
            const newData = data.map((item) => {
                const _workWeek = WEEKDAY_NAMES.map((_, index) => {
                    let value = item.workWeek?.find((x) => x.dayOfWeek === index)
                    if (value) {
                        value = {
                            ...value,
                            name: WEEKDAY_NAMES[index],
                            enabled: true,
                            startTime: value.startTime ? moment(todayStr + " " + value.startTime) : undefined,
                            endTime: value.endTime ? moment(todayStr + " " + value.endTime) : undefined,
                            allDay: value.allDay,
                            maxHoursEnabled: !!value.maxHoursEnabled,
                            distEdEnabled: !!value.distEdEnabled,
                            maxHours: value.maxHours?.toString(),
                            bonusMinutesEnabled: !!value.bonusMinutesEnabled,
                            bonusMinutesBefore: value.bonusMinutesBefore?.toString(),
                            bonusMinutesAfter: value.bonusMinutesAfter?.toString(),
                            breakTimes: (value.breakTimes || []).map(breakTimePayloadToForm)
                        }
                    } else {
                        value = {
                            dayOfWeek: index,
                            name: WEEKDAY_NAMES[index],
                            enabled: false,
                            allDay: false,
                            maxHoursEnabled: false,
                            distEdEnabled: false,
                            maxHours: undefined,
                            bonusMinutesEnabled: false,
                            bonusMinutesBefore: undefined,
                            bonusMinutesAfter: undefined,
                            breakTimes: []
                        }
                    }
                    return value
                })
                item.workWeek = _workWeek
                item.id = item.shiftId
                item.isActiveHtml = renderIsActiveHtml(item.isActive, true)
                item.nameHtml = renderName(item)
                item.remoteMakeupMinutesHtml = renderNumber(item.remoteMakeupMinutes)
                item.maxWeeklyFlexHoursHtml = renderNumber(item.maxWeeklyFlexHours)
                item.maxWeeklyDistEdHoursHtml = renderNumber(item.maxWeeklyDistEdHours)
                item.startTimeHtml = item.startTime ? moment(todayStr + " " + item.startTime).format(timeFormat) : null
                item.endTimeHtml = item.endTime ? moment(todayStr + " " + item.endTime).format(timeFormat) : null
                item.programsHtml = renderProgramsInput({
                    programs: item.programs,
                    campuses: item.campuses,
                    disabled: true
                })
                item.programVersionsHtml = renderProgramVersionsInput({
                    programs: item.programs,
                    programVersions: item.programVersions,
                    disabled: true
                })
                item.campusesHtml = renderCampusesInput({
                    campuses: item.campuses,
                    programs: item.programs,
                    disabled: true
                })
                item.isAutoAssignShiftHtml = renderCheckbox({
                    value: item.isAutoAssignShift,
                    disabled: true,
                    field: "isAutoAssignShift"
                })
                item.isFlexShiftHtml = renderCheckbox({
                    value: item.isFlexShift,
                    disabled: true,
                    field: "isFlexShift"
                })
                item.isFacialRecognitionEnabledHtml = renderCheckbox({
                    value: item.isFacialRecognitionEnabled,
                    disabled: true,
                    field: "isFacialRecognitionEnabled"
                })
                item.isSpecialEventEnabledHtml = renderCheckbox({
                    value: item.isSpecialEventEnabled,
                    disabled: true,
                    field: "isSpecialEventEnabled"
                })
                return item
            })
            return {data: newData, total}
        },
        {
            initialData: {data: [], total: 0},
            onSuccess: (result) => {
                setNewData(null)
                setOldData(null)
                currentNewData.current = null
                currentOldData.current = null
                dispatch({data: result.data, total: result.total})
            },
            onError: (err) => {
                handleError(err)
            }
        }
    )

    const onChangeFilter = (key, value) => {
        const newFilter = {...currentFilter}
        newFilter[key] = value
        setCurrentFilter(newFilter)
    }

    const onApplyFilter = () => {
        applyFilter()
        settingsQuery.refetch()
    }

    const createOrEditShiftMutation = useMutation(
        async (payload: {shiftId?: number; isEdit: boolean; data: any}) => {
            if (!payload.isEdit) {
                return shiftsService.create(payload.data)
            }
            return shiftsService.update({...payload.data, shiftId: payload.shiftId})
        },
        {
            onSuccess: () => {
                if (pendingCloseBulkPopupRef.current) {
                    bulkBreakTimesPopup.close()
                    pendingCloseBulkPopupRef.current = false
                }
                setNewData(null)
                setOldData(null)
                currentNewData.current = null
                currentOldData.current = null
                settingsQuery.refetch()
            },
            onError: (error) => {
                pendingCloseBulkPopupRef.current = false
                handleError(error)
            }
        }
    )

    const deleteShiftMutation = useMutation(
        async () => {
            const data = getCurrentData()
            const checkedItems = data.filter((item) => item.isChecked)
            const payload = checkedItems.map((item) => item.shiftId)
            dispatch({isShowTableHeaderAction: false, isHideMenuActions: false})
            return shiftsService.delete({ids: payload})
        },
        {
            onSuccess: () => {
                settingsQuery.refetch()
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const manualAssignShiftMutation = useMutation(
        async (payload: {shiftIds: number[]; campusIds: number[]; majorIds: number[]; majorVersionIds: number[]}) => {
            dispatch({isShowTableHeaderAction: false, isHideMenuActions: false})
            return shiftsService.manualAssign(payload)
        },
        {
            onSuccess: () => {
                settingsQuery.refetch()
                popupConfirmManualAssignShift.close()
                toastSuccess("Assign shift successfully")
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const isLoading =
        !!settingsQuery.isFetching ||
        !!deleteShiftMutation.isLoading ||
        !!createOrEditShiftMutation.isLoading ||
        !!checkFacialRecognitionQuery.isFetching

    const getColumns = () => {
        let columns: any[] = [
            {title: t("settings.name"), field: "nameHtml", sortable: true, orderField: "name"},
            {title: t("settings.isAutoAssignShift"), field: "isAutoAssignShiftHtml", style: {minWidth: "100px"}}
        ]

        if (checkFacialRecognitionQuery.data) {
            columns.push({
                title: t("settings.isFacialRecognitionEnabled"),
                field: "isFacialRecognitionEnabledHtml",
                style: {minWidth: "100px"}
            })
        }

        return [
            ...columns,
            ...[
                {title: t("settings.isFlexShift"), field: "isFlexShiftHtml", style: {minWidth: "100px"}},
                {title: t("settings.maxWeeklyFlexHours"), field: "maxWeeklyFlexHoursHtml", style: {minWidth: "150px"}},
                {
                    title: t("settings.maxWeeklyDistEdHours"),
                    field: "maxWeeklyDistEdHoursHtml",
                    style: {minWidth: "150px"}
                },
                {
                    title: t("settings.remoteMakeupMinutes"),
                    field: "remoteMakeupMinutesHtml",
                    style: {minWidth: "150px"}
                },
                {
                    title: t("settings.isActive"),
                    field: "isActiveHtml",
                    sortable: true,
                    style: {minWidth: "80px"},
                    orderField: "isActive"
                },
                {
                    title: t("settings.isSpecialEventEnabled"),
                    field: "isSpecialEventEnabledHtml",
                    style: {minWidth: "100px"},
                    sortable: true,
                    orderField: "isSpecialEventEnabled"
                },
                {title: t("settings.campuses"), field: "campusesHtml", style: {minWidth: "350px"}},
                {title: t("settings.programs"), field: "programsHtml", style: {minWidth: "350px"}},
                {title: t("settings.programVersions"), field: "programVersionsHtml", style: {minWidth: "350px"}}
            ]
        ]
    }

    const getPageTitle = () => {
        return t("settings.shifts")
    }
    const getFields = () => {
        const fields = [t("settings.name"), t("settings.isAutoAssignShift")]

        if (checkFacialRecognitionQuery.data) {
            fields.push(t("settings.isFacialRecognitionEnabled"))
        }

        return [
            ...fields,
            ...[
                t("settings.isFlexShift"),
                t("settings.maxWeeklyFlexHours"),
                t("settings.maxWeeklyDistEdHours"),
                t("settings.remoteMakeupMinutes"),
                t("settings.isActive"),
                t("settings.isSpecialEventEnabled"),
                t("settings.campuses"),
                t("settings.programs"),
                t("settings.programVersions")
            ]
        ]
    }

    const getTableHeaderActions = (isShowDuplicateBtn = false, checkedData = []) => {
        const hasLockedItem = (checkedData || []).some((item) => item.isLock)
        const actions = []
        if (!hasLockedItem) {
            actions.push({
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            })
        }
        return actions
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getListData", func: settingsQuery.refetch},
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickDeleteMulti", func: deleteShiftMutation.mutate},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete}
        ])
    }, [checkFacialRecognitionQuery.data])

    const renderCheckbox = (payload: {value: boolean; disabled?: boolean; field: string}) => {
        const {value, disabled, field} = payload

        if (disabled) {
            return (
                <div className={styles.inputWrap}>
                    <div className={styles.visibleCheckboxWrap}>
                        <Checkbox disabled checked={value} />
                    </div>
                </div>
            )
        }
        return (
            <div className={styles.inputWrap}>
                <div className={styles.visibleCheckboxWrap}>
                    <Checkbox checked={value} onChange={(event) => onChangeNewItemData(field, event.target.checked)} />
                </div>
            </div>
        )
    }

    const renderIsActiveHtml = (value, disabled: boolean = false) => {
        if (disabled) {
            return (
                <div className={styles.inputWrap}>
                    <div className={styles.visibleCheckboxWrap}>
                        <Checkbox disabled checked={value} />
                    </div>
                </div>
            )
        }

        return (
            <div className={styles.inputWrap}>
                <div className={styles.visibleCheckboxWrap}>
                    <Checkbox
                        checked={value}
                        onChange={(event) => onChangeNewItemData("isActive", event.target.checked)}
                    />
                </div>
            </div>
        )
    }

    const getMenuActions = () => {
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: onClickEdit,
                canShow: () => {
                    return true
                }
            },
            {
                title: "Manual Assign Shift ",
                icon: "EDIT",
                action: onManualAssignShift,
                canShow: () => {
                    return true
                }
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: onClickDelete,
                canShow: () => {
                    return true
                }
            }
        ]
    }

    const onManualAssignShift = (editItem) => {
        popupConfirmManualAssignShift.open()
        const majorIds = editItem.programs?.map((p) => p.id) || []
        const campusIds = editItem.campuses?.map((c) => c.id) || []
        const majorVersionIds = editItem.programVersions?.map((p) => p.id) || []

        setCurrentShiftSelected({
            shiftId: editItem.shiftId,
            campusIds,
            majorIds,
            majorVersionIds
        })
    }

    const onClickEdit = (editItem) => {
        const newItemData = cloneDeep(currentNewData.current)
        if (newItemData) {
            toastWarning(t("common:validation.saveBeforeEdit"))
            return
        }
        setNewData(
            cloneDeep({
                ...editItem,
                name: editItem.name,
                startTime: editItem.startTime,
                endTime: editItem.endTime,
                isActive: !!editItem.isActive,
                isAutoAssignShift: !!editItem.isAutoAssignShift,
                isFlexShift: !!editItem.isFlexShift,
                isFacialRecognitionEnabled: !!editItem.isFacialRecognitionEnabled,
                isSpecialEventEnabled: !!editItem.isSpecialEventEnabled
            })
        )
        setOldData(cloneDeep(editItem))
        currentNewData.current = cloneDeep({
            ...editItem,
            name: editItem.name,
            startTime: editItem.startTime,
            endTime: editItem.endTime,
            isActive: !!editItem.isActive,
            isAutoAssignShift: !!editItem.isAutoAssignShift,
            isSpecialEventEnabled: !!editItem.isSpecialEventEnabled
        })
        currentOldData.current = cloneDeep(editItem)
        const data = getCurrentData()
        const newItem = data.map((item) => {
            if (item.shiftId === editItem.shiftId) {
                item.isActiveHtml = renderIsActiveHtml(editItem.isActive)
                item.nameHtml = renderNameInput({value: editItem.name, workWeek: editItem.workWeek})
                item.isAutoAssignShiftHtml = renderCheckbox({
                    value: editItem.isAutoAssignShift,
                    field: "isAutoAssignShift"
                })
                item.isFlexShiftHtml = renderCheckbox({value: editItem.isFlexShift, field: "isFlexShift"})
                item.maxWeeklyDistEdHoursHtml = renderNumberInput({
                    value: editItem.maxWeeklyDistEdHours,
                    field: "maxWeeklyDistEdHours",
                    placeholder: "Number"
                })
                item.isFacialRecognitionEnabledHtml = renderCheckbox({
                    value: editItem.isFacialRecognitionEnabled,
                    field: "isFacialRecognitionEnabled"
                })
                item.isSpecialEventEnabledHtml = renderCheckbox({
                    value: editItem.isSpecialEventEnabled,
                    field: "isSpecialEventEnabled"
                })
                item.remoteMakeupMinutesHtml = renderNumberInput({
                    value: editItem.remoteMakeupMinutes,
                    field: "remoteMakeupMinutes",
                    placeholder: "Number"
                })
                item.maxWeeklyFlexHoursHtml = renderNumberInput({
                    value: editItem.maxWeeklyFlexHours,
                    field: "maxWeeklyFlexHours",
                    placeholder: "Number"
                })
                item.startTimeHtml = renderTimeInput({
                    key: "startTime",
                    startTime: editItem.startTime,
                    endTime: editItem.endTime
                })
                item.endTimeHtml = renderTimeInput({
                    key: "endTime",
                    startTime: editItem.startTime,
                    endTime: editItem.endTime
                })
                item.programsHtml = renderProgramsInput({programs: editItem.programs, campuses: editItem.campuses})
                item.campusesHtml = renderCampusesInput({campuses: editItem.campuses, programs: editItem.programs})
                item.programVersionsHtml = renderProgramVersionsInput({
                    programVersions: editItem.programVersions,
                    programs: editItem.programs
                })
                return item
            }
            return item
        })
        dispatch({data: newItem})
    }

    const onClickDelete = async (deletedItem) => {
        if (deletedItem.isForm) {
            setNewData(null)
            setOldData(null)
            currentNewData.current = null
            currentOldData.current = null
            settingsQuery.refetch()
            return
        }
        try {
            dispatch({isLoading: true})
            setNewData(null)
            setOldData(null)
            currentNewData.current = null
            currentOldData.current = null
            await shiftsService.delete({ids: [deletedItem.shiftId]})
            settingsQuery.refetch()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onChangeNewItemData = (key: string, value: any) => {
        currentNewData.current[key] = value
        setNewData(cloneDeep(currentNewData.current))
        const data = getCurrentData()
        const newItem = data.map((item) => {
            if (item.id === currentNewData.current.id) {
                item.nameHtml = renderNameInput({
                    value: currentNewData.current.name,
                    workWeek: currentNewData.current.workWeek
                })
                item.remoteMakeupMinutesHtml = renderNumberInput({
                    value: currentNewData.current.remoteMakeupMinutes,
                    field: "remoteMakeupMinutes",
                    placeholder: "Number"
                })
                item.maxWeeklyFlexHoursHtml = renderNumberInput({
                    value: currentNewData.current.maxWeeklyFlexHours,
                    field: "maxWeeklyFlexHours",
                    placeholder: "Number"
                })
                item.maxWeeklyDistEdHoursHtml = renderNumberInput({
                    value: currentNewData.current.maxWeeklyDistEdHours,
                    field: "maxWeeklyDistEdHours",
                    placeholder: "Number"
                })
                item.startTimeHtml = renderTimeInput({
                    key: "startTime",
                    startTime: currentNewData.current.startTime,
                    endTime: currentNewData.current.endTime
                })
                item.endTimeHtml = renderTimeInput({
                    key: "endTime",
                    endTime: currentNewData.current.endTime,
                    startTime: currentNewData.current.startTime
                })
                item.isActiveHtml = renderIsActiveHtml(currentNewData.current.isActive)
                item.isAutoAssignShiftHtml = renderCheckbox({
                    value: currentNewData.current.isAutoAssignShift,
                    field: "isAutoAssignShift"
                })
                item.isFlexShiftHtml = renderCheckbox({value: currentNewData.current.isFlexShift, field: "isFlexShift"})
                item.isFacialRecognitionEnabledHtml = renderCheckbox({
                    value: currentNewData.current.isFacialRecognitionEnabled,
                    field: "isFacialRecognitionEnabled"
                })
                item.isSpecialEventEnabledHtml = renderCheckbox({
                    value: currentNewData.current.isSpecialEventEnabled,
                    field: "isSpecialEventEnabled"
                })
                item.programsHtml = renderProgramsInput({
                    programs: currentNewData.current.programs,
                    campuses: currentNewData.current.campuses
                })
                item.programVersionsHtml = renderProgramVersionsInput({
                    programVersions: currentNewData.current.programVersions,
                    programs: currentNewData.current.programs
                })
                item.campusesHtml = renderCampusesInput({
                    campuses: currentNewData.current.campuses,
                    programs: currentNewData.current.programs
                })
                return item
            }
            return item
        })
        dispatch({data: cloneDeep(newItem)})
    }

    const onApplyBulkBreakTimes = (breakTimes: BreakTimeForm[]) => {
        const workWeek = bulkBreakTimesWorkWeekRef.current
        const clonedBreakTimes = cloneBreakTimes(breakTimes)
        const updatedWorkWeek = workWeek.map((day) => ({
            ...day,
            breakTimes: cloneBreakTimes(clonedBreakTimes)
        }))
        onChangeNewItemData("workWeek", updatedWorkWeek)
        pendingCloseBulkPopupRef.current = true
        const isSaved = saveCurrentShift(updatedWorkWeek)
        if (!isSaved) {
            pendingCloseBulkPopupRef.current = false
        }
    }

    const buildWorkWeekPayload = (workdays: WorkDay[]) =>
        workdays.map((x) => ({
            dayOfWeek: x.dayOfWeek,
            allDay: x.allDay,
            maxHoursEnabled: !!x.maxHoursEnabled,
            distEdEnabled: !!x.distEdEnabled,
            maxHours: x.maxHours?.toString()?.length ? parseFloat(String(x.maxHours)) : undefined,
            bonusMinutesEnabled: !!x.bonusMinutesEnabled,
            bonusMinutesBefore: x.bonusMinutesBefore?.toString()?.length
                ? parseFloat(String(x.bonusMinutesBefore))
                : undefined,
            bonusMinutesAfter: x.bonusMinutesAfter?.toString()?.length
                ? parseFloat(String(x.bonusMinutesAfter))
                : undefined,
            startTime: x.startTime?.format("HH:mm"),
            endTime: x.endTime?.format("HH:mm"),
            breakTimes: (x.breakTimes || []).map(breakTimeFormToPayload)
        }))

    const saveCurrentShift = (workWeekOverride?: WorkDay[]): boolean => {
        const shiftData = currentNewData.current || newData
        if (!shiftData) {
            return false
        }

        const dataForValidate = {
            ...shiftData,
            workWeek: workWeekOverride || shiftData.workWeek
        }

        if (!validateDataBeforeSubmit(dataForValidate)) {
            return false
        }

        const {
            name,
            startTime,
            endTime,
            isActive,
            isAutoAssignShift,
            isForm,
            id,
            programs,
            programVersions,
            workWeek,
            campuses,
            remoteMakeupMinutes,
            maxWeeklyFlexHours,
            maxWeeklyDistEdHours,
            isFlexShift,
            isFacialRecognitionEnabled,
            isSpecialEventEnabled
        } = dataForValidate

        const workdays = (workWeekOverride || workWeek).filter((x) => x.enabled === true)
        const submitData = {
            name,
            startTime: startTime || undefined,
            endTime: endTime || undefined,
            isActive,
            isAutoAssignShift,
            programIds: programs?.map((p) => p.id) || [],
            campusIds: campuses?.map((c) => c.id) || [],
            programVersionIds: programVersions?.map((p) => p.id) || [],
            workWeek: buildWorkWeekPayload(workdays),
            remoteMakeupMinutes: remoteMakeupMinutes?.toString()?.length ? Number(remoteMakeupMinutes) : null,
            maxWeeklyFlexHours: maxWeeklyFlexHours?.toString()?.length ? Number(maxWeeklyFlexHours) : null,
            isFlexShift: !!isFlexShift,
            isFacialRecognitionEnabled: !!isFacialRecognitionEnabled,
            isSpecialEventEnabled: !!isSpecialEventEnabled,
            maxWeeklyDistEdHours: maxWeeklyDistEdHours?.toString()?.length ? Number(maxWeeklyDistEdHours) : null
        }

        if (isForm) {
            createSetting(submitData)
        } else {
            updateSetting(id, submitData)
        }
        return true
    }

    const renderName = (item: {name: string; workWeek: WorkDay[]; [key: string]: any}) => {
        return (
            <div className={styles.inputWrap}>
                <div className={styles.nameRow}>
                    <button
                        type="button"
                        className={styles.editNameBtn}
                        title={t("common:action.edit")}
                        onClick={(event) => {
                            event.stopPropagation()
                            onClickEdit(item)
                        }}>
                        <Icon icon="EDIT_LINE" size={18} />
                    </button>
                    <div className={styles.name}>{item.name}</div>
                </div>
                {renderWorkWeek({disabled: true, workWeek: item.workWeek})}
            </div>
        )
    }

    const renderWorkWeek = (payload: {
        disabled?: boolean
        workWeek: WorkDay[]
        breakTimesFieldErrors?: BreakTimesFieldErrorsByDay
    }) => {
        const {
            disabled,
            workWeek = INITIAL_WORK_WEEK,
            breakTimesFieldErrors = breakTimesFieldErrorsRef.current
        } = payload ?? {}
        const orderedWorkWeek = orderWorkWeekByStartDay(workWeek, startWeekOn)

        const onOpenBulkBreakTimes = () => {
            bulkBreakTimesWorkWeekRef.current = orderedWorkWeek
            const sourceDay =
                orderedWorkWeek.find((day) => day.breakTimes?.length) || orderedWorkWeek.find((day) => day.enabled)
            setBulkBreakTimesInitial(cloneBreakTimes(sourceDay?.breakTimes || []))
            bulkBreakTimesPopup.open()
        }

        return (
            <Collapse className={styles.panel}>
                <Panel header="Define WorkDays & Time" key="1" className={styles.panel}>
                    {!disabled ? (
                        <div className={styles.bulkBreakTimesAction}>
                            <SecondaryButton title="Set Break Times for All Days" onClick={onOpenBulkBreakTimes} />
                        </div>
                    ) : null}
                    <div className={disabled ? styles.workWeekName : styles.workweekContainer}>
                        {orderedWorkWeek.map((item) => {
                            return (
                                <WorkDayItem
                                    key={item.dayOfWeek}
                                    isShiftSetting
                                    disabled={disabled}
                                    item={item}
                                    breakTimeFieldErrors={breakTimesFieldErrors[item.dayOfWeek] || {}}
                                    onChange={(_workDay) => {
                                        const _workWeek = orderedWorkWeek.map((day) =>
                                            day.dayOfWeek === _workDay.dayOfWeek ? _workDay : day
                                        )
                                        if (breakTimesFieldErrorsRef.current[item.dayOfWeek]) {
                                            const nextErrors = {...breakTimesFieldErrorsRef.current}
                                            delete nextErrors[item.dayOfWeek]
                                            breakTimesFieldErrorsRef.current = nextErrors
                                        }
                                        onChangeNewItemData("workWeek", _workWeek)
                                    }}
                                />
                            )
                        })}
                    </div>
                </Panel>
            </Collapse>
        )
    }

    const renderNameInput = (payload: {
        value: string
        workWeek?: any[]
        disabled?: boolean
        breakTimesFieldErrors?: BreakTimesFieldErrorsByDay
    }) => {
        const {value, workWeek, disabled, breakTimesFieldErrors} = payload

        return (
            <div className={styles.inputWrap}>
                <BaseInput
                    value={value}
                    style={{width: "380px"}}
                    placeholder="Name"
                    onChange={(value) => onChangeNewItemData("name", value)}
                />
                {renderWorkWeek({
                    workWeek,
                    disabled,
                    breakTimesFieldErrors
                })}
            </div>
        )
    }

    const renderNumber = (value: any) => {
        return (
            <div className={styles.inputWrap}>
                <div className={styles.numberValue}>{value ?? ""}</div>
            </div>
        )
    }

    const renderNumberInput = (payload: {value: any; field: string; placeholder?: string}) => {
        const {value, field, placeholder} = payload

        return (
            <div className={styles.inputWrap}>
                <BaseInput
                    value={value}
                    type="number"
                    placeholder={placeholder}
                    onChange={(value) => onChangeNewItemData(field, value)}
                />
            </div>
        )
    }

    const renderTimeInput = (payload: {key: "startTime" | "endTime"; startTime: string; endTime: string}) => {
        const {key} = payload
        const todayStr = moment().format("YYYY-MM-DD")
        const startTime = payload["startTime"] ? moment(todayStr + " " + payload["startTime"]) : undefined
        const endTime = payload["endTime"] ? moment(todayStr + " " + payload["endTime"]) : undefined
        const format = model.getUserTimeFormat()

        return (
            <BaseTimePicker
                key={key}
                value={key === "startTime" ? startTime : endTime}
                onChange={(newValue) => {
                    const data = newValue ? moment(newValue).format(format) : null
                    const value = newValue ? moment(newValue) : null

                    if (
                        value &&
                        ((key === "startTime" && endTime && value.isAfter(endTime)) ||
                            (key === "endTime" && startTime && startTime.isAfter(value)))
                    ) {
                        onChangeNewItemData(key === "startTime" ? "endTime" : "startTime", data)
                    }

                    onChangeNewItemData(key, data)
                }}
                disabledHours={key === "endTime" ? () => getDisabledEndHours(startTime) : undefined}
                disabledMinutes={key === "endTime" ? () => getDisabledEndMinutes(startTime, endTime) : undefined}
                placeholder={key === "startTime" ? "From" : "To"}
                format={format}
                className={styles.timePicker}
                size="small"
            />
        )
    }

    const renderCampusesInput = (payload: {campuses: any[]; programs: any[]; disabled?: boolean}) => {
        const {campuses, programs, disabled} = payload

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "350px"}}>
                    <CampusSelect
                        isDisabled={!!disabled}
                        displayOnlyActiveCampuses
                        className={styles.select}
                        isClearable
                        isMulti
                        value={campuses}
                        onChange={(data) => {
                            const campusIds = data?.map((item) => item.id)
                            const newPrograms = programs?.filter((item) => {
                                return item.campusIds?.some((campusId) => campusIds?.includes(campusId))
                            })
                            onChangeNewItemData("programs", newPrograms)
                            onChangeNewItemData("campuses", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    const renderProgramsInput = (payload: {programs: any[]; campuses: any[]; disabled?: boolean}) => {
        const {programs, campuses, disabled} = payload
        const campusIds = campuses?.map((item) => item.id)

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "350px"}}>
                    <ProgramSelect
                        isDisabled={!!disabled}
                        className={styles.select}
                        isClearable
                        isActive
                        campusId={campusIds}
                        isMulti
                        value={programs}
                        onChange={(data) => {
                            onChangeNewItemData("programs", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    const renderProgramVersionsInput = (payload: {programs: any[]; programVersions: any[]; disabled?: boolean}) => {
        const {programs, programVersions, disabled} = payload
        const programIds = programs?.map((item) => item.id)

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "350px"}}>
                    <ProgramVersionSelect
                        isDisabled={!!disabled}
                        className={styles.select}
                        isClearable
                        isActive
                        programId={programIds}
                        isMulti
                        value={programVersions}
                        onChange={(data) => {
                            onChangeNewItemData("programVersions", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    const onClickAddItem = () => {
        const newItem = {
            id: uuid(),
            nameHtml: renderNameInput({value: ""}),
            remoteMakeupMinutesHtml: renderNumberInput({
                value: "",
                field: "remoteMakeupMinutes",
                placeholder: "Number"
            }),
            maxWeeklyFlexHoursHtml: renderNumberInput({
                value: "",
                field: "maxWeeklyFlexHours",
                placeholder: "Number"
            }),
            startTimeHtml: renderTimeInput({key: "startTime", startTime: undefined, endTime: undefined}),
            endTimeHtml: renderTimeInput({key: "endTime", startTime: undefined, endTime: undefined}),
            isActive: false,
            isActiveHtml: renderIsActiveHtml(false),
            isAutoAssignShiftHtml: renderCheckbox({
                value: false,
                disabled: false,
                field: "isAutoAssignShift"
            }),
            isFlexShiftHtml: renderCheckbox({
                value: false,
                disabled: false,
                field: "isFlexShift"
            }),
            maxWeeklyDistEdHoursHtml: renderNumberInput({
                value: "",
                field: "maxWeeklyDistEdHours",
                placeholder: "Number"
            }),
            isFacialRecognitionEnabledHtml: renderCheckbox({
                value: false,
                disabled: false,
                field: "isFacialRecognitionEnabled"
            }),
            isSpecialEventEnabled: false,
            isSpecialEventEnabledHtml: renderCheckbox({
                value: false,
                disabled: false,
                field: "isSpecialEventEnabled"
            }),
            campusesHtml: renderCampusesInput({campuses: [], programs: [], disabled: false}),
            workWeek: INITIAL_WORK_WEEK,
            programsHtml: renderProgramsInput({programs: [], campuses: [], disabled: false}),
            programVersionsHtml: renderProgramVersionsInput({programs: [], programVersions: [], disabled: false}),
            isForm: true
        }
        const data = getCurrentData()
        data.push(newItem)
        setNewData(newItem)
        currentNewData.current = cloneDeep(newItem)
        setOldData(null)
        currentOldData.current = null
        dispatch({data: cloneDeep(data)})
        scrollTableToBottom(props.tableRef)
    }

    const validateDataBeforeSubmit = (dataToValidate = newData) => {
        const {isActive, name, workWeek} = dataToValidate

        let error: string

        if (!name) {
            error = t("common:validation.cantEmpty", {
                field: t("settings.name")
            })
        }

        if (!isBoolean(isActive)) {
            error = t("common:validation.cantEmpty", {
                field: t("settings.isActive")
            })
        }

        const {byDay, details} = collectBreakTimesValidationErrors(workWeek || [])
        if (details.length > 0) {
            breakTimesFieldErrorsRef.current = byDay
            const shiftName = name || "Untitled shift"
            const locations = details
                .map(
                    (item) =>
                        `${item.dayName} Break Time #${item.breakTimeIndex + 1} (${formatBreakTimeFieldErrorLabel(
                            item.fields
                        )})`
                )
                .join("; ")
            error = `Shift "${shiftName}": required fields missing — ${locations}`

            const data = getCurrentData()
            const refreshedData = data.map((item) => {
                if (item.id === currentNewData.current?.id) {
                    return {
                        ...item,
                        nameHtml: renderNameInput({
                            value: currentNewData.current.name,
                            workWeek: workWeek || currentNewData.current.workWeek,
                            breakTimesFieldErrors: byDay
                        })
                    }
                }
                return item
            })
            dispatch({data: cloneDeep(refreshedData)})
        } else {
            breakTimesFieldErrorsRef.current = {}
        }

        if (error) {
            toastError(error)
            return false
        }
        return true
    }

    const onClickSave = async () => {
        saveCurrentShift()
    }

    const createSetting = async (data) => {
        createOrEditShiftMutation.mutate({
            isEdit: false,
            data
        })
    }

    const updateSetting = async (shiftId, data) => {
        createOrEditShiftMutation.mutate({
            shiftId,
            isEdit: true,
            data
        })
    }

    const onClickCancel = () => {
        const oldItemData = oldData
        const data = getCurrentData()
        const newData = data
            .filter((item) => !item.isForm)
            .map((item) => {
                if (oldItemData && oldItemData.id === item.id) {
                    item.nameHtml = renderName(oldItemData)
                    item.startTimeHtml = oldItemData.startTime
                    item.endTimeHtml = oldItemData.endTime
                    item.isActive = oldItemData.isActive
                    item.isAutoAssignShift = oldItemData.isAutoAssignShift
                    item.isFlexShift = oldItemData.isFlexShift
                    item.isFacialRecognitionEnabled = oldItemData.isFacialRecognitionEnabled
                    item.isSpecialEventEnabled = oldItemData.isSpecialEventEnabled
                    item.isActiveHtml = renderIsActiveHtml(oldItemData.isActive, true)
                    item.isAutoAssignShiftHtml = renderCheckbox({
                        value: oldItemData.isAutoAssignShift,
                        disabled: true,
                        field: "isAutoAssignShift"
                    })
                    item.isFlexShiftHtml = renderCheckbox({
                        value: oldItemData.isFlexShift,
                        disabled: true,
                        field: "isFlexShift"
                    })
                    item.isFacialRecognitionEnabledHtml = renderCheckbox({
                        value: oldItemData.isFacialRecognitionEnabled,
                        disabled: true,
                        field: "isFacialRecognitionEnabled"
                    })
                    item.isSpecialEventEnabledHtml = renderCheckbox({
                        value: oldItemData.isSpecialEventEnabled,
                        disabled: true,
                        field: "isSpecialEventEnabled"
                    })
                    item.remoteMakeupMinutesHtml = renderNumber(oldItemData.remoteMakeupMinutes)
                    item.maxWeeklyFlexHoursHtml = renderNumber(oldItemData.maxWeeklyFlexHours)
                    item.maxWeeklyDistEdHoursHtml = renderNumber(oldItemData.maxWeeklyDistEdHours)
                    item.programsHtml = renderProgramsInput({
                        programs: oldItemData.programs,
                        campuses: oldItemData.campuses,
                        disabled: true
                    })
                    item.programVersionsHtml = renderProgramVersionsInput({
                        programs: oldItemData.programs,
                        programVersions: oldItemData.programVersions,
                        disabled: true
                    })
                    item.campusesHtml = renderCampusesInput({
                        campuses: oldItemData.campuses,
                        programs: oldItemData.programs,
                        disabled: true
                    })
                    return item
                }
                return item
            })
        setNewData(null)
        currentNewData.current = null
        setOldData(null)
        currentOldData.current = null
        dispatch({data: newData})
    }

    const onConfirmManualAssignShift = () => {
        if (currentShiftSelected) {
            manualAssignShiftMutation.mutate({
                shiftIds: [currentShiftSelected.shiftId],
                campusIds: currentShiftSelected.campusIds,
                majorIds: currentShiftSelected.majorIds,
                majorVersionIds: currentShiftSelected.majorVersionIds
            })
        }
    }

    const onCancelManualAssignShift = () => {
        popupConfirmManualAssignShift.close()
    }

    return (
        <div className={styles.wrapper}>
            <Filter
                searchValue={searchValue}
                changeSearch={changeSearch}
                onApplyFilter={onApplyFilter}
                clearFilter={clearFilter}
                currentFilter={currentFilter}
                onChangeFilter={onChangeFilter}
                setCurrentFilter={setCurrentFilter}
            />

            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                menuActions={isHideMenuActions ? [] : menuActions}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn
                tableRef={props.tableRef}
                tableClassName={styles.studentTable}
                className={styles.shiftTable}
                orderField={orderField}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onClickSortColumn={onClickSortColumn}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />
            {!newData ? (
                !isLoading && <AddItemCircleButton onClick={onClickAddItem} />
            ) : (
                <div className={styles.buttonWrap}>
                    <SecondaryButton
                        title={t("common:action.cancel")}
                        onClick={onClickCancel}
                        className={styles.cancelBtn}
                    />
                    <BaseButton
                        title={t("common:action.save").toUpperCase()}
                        onClick={onClickSave}
                        loading={isLoading}
                    />
                </div>
            )}
            <ConfirmPopup
                isVisible={popupConfirmManualAssignShift.isVisible}
                title={"Are you sure?"}
                loading={manualAssignShiftMutation.isLoading}
                onClose={onCancelManualAssignShift}
                onConfirm={onConfirmManualAssignShift}
            />
            <ShiftBreakTimesPopup
                isVisible={bulkBreakTimesPopup.isVisible}
                initialBreakTimes={bulkBreakTimesInitial}
                loading={createOrEditShiftMutation.isLoading}
                onClose={bulkBreakTimesPopup.close}
                onApply={onApplyBulkBreakTimes}
            />
        </div>
    )
}

export const Shift = KlassappTableHOC(ShiftTab)
